
import React, { useRef, useState } from 'react';
import { Project, TranslationFile } from '../types';
import { FolderPlus, Folder, ChevronRight, FileText, CheckCircle, Clock, Upload, FolderOpen, BrainCircuit, Languages, Trash2, Lock, ArrowRight, ChevronLeft, HelpCircle, Sparkles } from 'lucide-react';
import { getLanguage } from '../utils/languages';
import { useTourStore } from '../store/useTourStore';
import { DEMO_PROJECT_ID } from '../utils/demoProject';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

interface ProjectsListProps {
    projects: Project[];
    files: TranslationFile[];
    onSelectProject: (projectId: string) => void;
    onCreateProject: (type?: Project) => void; // Updated to receive optional type
    onImportProject: (file: File) => void;
    onOpenFolder?: (files: File[]) => void;
    onNavigateToLab: () => void;
    onDeleteProject?: (projectId: string) => void;
    fsStatus?: { isConnected: boolean; folderName?: string };
    onConnectFS?: () => void;
    mode?: 'translate' | 'train';
}

const ProjectsList: React.FC<ProjectsListProps> = ({
    projects,
    files,
    onSelectProject,
    onCreateProject,
    onImportProject,
    onOpenFolder,
    onDeleteProject,
    fsStatus,
    onConnectFS,
    mode = 'translate'
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 8;

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.name.endsWith('.zip')) {
            onImportProject(file);
        }
        // Reset input so same file can be selected again
        e.target.value = '';
    };

    const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (fileList && fileList.length > 0 && onOpenFolder) {
            // Filter only .txt files
            const txtFiles = Array.from(fileList).filter((f: File) => f.name.endsWith('.txt'));
            onOpenFolder(txtFiles);
        }
        e.target.value = '';
    };


    const getProjectStats = (projectId: string) => {
        const projectFiles = files.filter(f => f.projectId === projectId);
        // "Completed" = translation done (QC optional, không phải blocker để file sẵn sàng download)
        const completed = projectFiles.filter(f => f.translationStatus === 'done').length;
        return { total: projectFiles.length, completed };
    };

    // Last activity = max(completedAt, createdAt) của file gần nhất trong project.
    // Fallback project.createdAt nếu project rỗng. Project type chưa có updatedAt field
    // nên compute trực tiếp ở đây thay vì sửa schema.
    const getProjectLastActivity = (project: Project): Date => {
        const projectFiles = files.filter(f => f.projectId === project.id);
        if (projectFiles.length === 0) {
            return new Date(project.createdAt);
        }
        const ts = projectFiles.map(f => {
            const c = f.completedAt ? new Date(f.completedAt).getTime() : 0;
            const cr = new Date(f.createdAt).getTime();
            return Math.max(c, cr);
        });
        return new Date(Math.max(...ts));
    };

    // Tour state — khi active sẽ filter list chỉ hiện demo project
    const tourStep = useTourStore(s => s.step);
    const tourActive = tourStep !== 'idle';
    const tourAdvance = useTourStore(s => s.advance);
    const tourEnd = useTourStore(s => s.end);
    const driverRef = useRef<Driver | null>(null);

    // Khi vào step 'create-modal' → highlight nút "Tạo dự án" trong modal
    React.useEffect(() => {
        if (tourStep !== 'create-modal') return;
        const t = setTimeout(() => {
            driverRef.current?.destroy();
            driverRef.current = driver({
                popoverClass: 'tour-list-popover', showProgress: false,
                allowClose: false,
                showButtons: [],
                onDestroyed: () => {
                    if (useTourStore.getState().step === 'create-modal') {
                        useTourStore.getState().end();
                    }
                },
                steps: [{
                    element: '[data-tour="create-modal-card"]',
                    popover: {
                        title: 'Bước 2/4 — Đặt tên rồi tạo ✏️',
                        description: 'Tên đã điền sẵn cho bạn. Bấm <b>"Tạo dự án"</b> đang nháy bên dưới để tiếp tục.',
                        side: 'right' as const,
                    },
                }],
            });
            driverRef.current.drive();
        }, 350);
        return () => clearTimeout(t);
    }, [tourStep]);

    // Khi vào step 'select-demo' → highlight row demo bằng driver.js
    React.useEffect(() => {
        if (tourStep !== 'select-demo') return;
        const t = setTimeout(() => {
            driverRef.current?.destroy();
            driverRef.current = driver({
                popoverClass: 'tour-list-popover', showProgress: false,
                allowClose: false,
                showButtons: [],
                onDestroyed: () => {
                    if (useTourStore.getState().step === 'select-demo') {
                        useTourStore.getState().end();
                    }
                },
                steps: [{
                    element: '[data-tour="demo-row"]',
                    popover: {
                        title: 'Bước 3/4 — Mở project demo 👉',
                        description: 'Project <b>demo có sẵn</b> đã chuẩn bị file mẫu. Bấm vào dòng này để mở.',
                        side: 'bottom' as const,
                        align: 'center' as const,
                    },
                }],
            });
            driverRef.current.drive();
        }, 300);
        return () => clearTimeout(t);
    }, [tourStep]);

    // Cleanup driver khi tour kết thúc
    React.useEffect(() => {
        if (tourStep === 'idle') {
            driverRef.current?.destroy();
            driverRef.current = null;
        }
    }, [tourStep]);

    // Restore tour state khi user back ra (rời project detail) trước khi xem hết
    React.useEffect(() => {
        return () => {
            if (driverRef.current) driverRef.current.destroy();
        };
    }, []);

    // Sort projects by last activity desc — newest activity first
    const sortedProjects = React.useMemo(() => {
        const base = [...projects].sort((a, b) =>
            getProjectLastActivity(b).getTime() - getProjectLastActivity(a).getTime()
        );
        // Trong tour mode → chỉ hiện demo
        if (tourActive) return base.filter(p => p.id === DEMO_PROJECT_ID);
        // Ngoài tour → ẩn demo project
        return base.filter(p => !p.isDemo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, files, tourActive]);

    const totalPages = Math.max(1, Math.ceil(sortedProjects.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const pagedProjects = sortedProjects.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

    const isTrainMode = mode === 'train';

    const textMap = {
        title: isTrainMode ? 'Huấn luyện AI' : 'Dự án',
        subtitle: isTrainMode ? 'Quản lý các dự án huấn luyện và tinh chỉnh mô hình' : 'Quản lý các dự án dịch thuật của bạn',
        emptyTitle: isTrainMode ? 'Chưa có dữ liệu huấn luyện' : 'Chưa có dự án nào',
        emptySub: isTrainMode ? 'Tạo dự án huấn luyện đầu tiên để bắt đầu' : 'Tạo dự án đầu tiên để bắt đầu dịch',
        btnCreate: isTrainMode ? 'Tạo huấn luyện' : 'Tạo dự án',
        btnNew: isTrainMode ? 'Dự án huấn luyện' : 'Dự án mới',
        colName: isTrainMode ? 'Tên bộ dữ liệu' : 'Tên dự án',
    };

    // Hard gate: chưa connect FS → chặn toàn bộ Projects List, bắt buộc connect trước khi dùng
    if (!fsStatus?.isConnected && onConnectFS) {
        return (
            <div className="h-full flex items-center justify-center p-6 bg-gradient-to-br from-indigo-50/40 via-white to-slate-50">
                <div className="max-w-md w-full bg-white rounded-2xl border border-indigo-200 shadow-xl p-8 text-center animate-in fade-in zoom-in-95 duration-300">
                    <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <FolderOpen className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-xl font-black text-slate-800 mb-2">
                        Kết nối thư mục để bắt đầu
                    </h2>
                    <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                        Dự án dịch được lưu <strong className="text-indigo-700">trực tiếp trên máy của bạn</strong> —
                        bảo mật hơn, không giới hạn dung lượng, xài offline được.
                        <br />
                        <span className="text-xs text-slate-500 mt-2 block">Bước này chỉ cần làm <strong>1 lần</strong> khi bắt đầu.</span>
                    </p>
                    {fsStatus?.error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 text-left">
                            <strong>Lỗi:</strong> {fsStatus.error}
                        </div>
                    )}
                    <button
                        onClick={onConnectFS}
                        className="w-full px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-black rounded-xl hover:shadow-lg hover:shadow-indigo-200 transition-all flex items-center justify-center gap-2"
                    >
                        <FolderOpen className="w-4 h-4" />
                        Chọn thư mục lưu dự án
                    </button>
                    <details className="mt-3 text-left">
                        <summary className="text-xs text-slate-500 cursor-pointer hover:text-indigo-600 select-none">
                            🔧 Chọn không được? Bấm vào đây
                        </summary>
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-slate-700 space-y-2">
                            <p>Browser có thể đang block File System Access. Mở trang setting Chrome:</p>
                            <button
                                type="button"
                                onClick={async (e) => {
                                    const url = 'chrome://settings/content/filesystem';
                                    try {
                                        await navigator.clipboard.writeText(url);
                                        const btn = e.currentTarget as HTMLButtonElement;
                                        const orig = btn.textContent;
                                        btn.textContent = '✓ Đã copy — paste vào thanh address bar';
                                        setTimeout(() => { btn.textContent = orig; }, 2500);
                                    } catch {
                                        // Fallback: thử mở tab mới (Chrome sẽ block chrome:// nhưng try)
                                        window.open(url, '_blank');
                                    }
                                }}
                                className="w-full px-3 py-2 bg-white border border-amber-300 rounded text-xs font-mono text-indigo-700 hover:bg-amber-100 text-left"
                            >
                                chrome://settings/content/filesystem
                            </button>
                            <p className="text-[11px] text-slate-500">
                                Bấm để copy link, paste vào thanh địa chỉ Chrome (Chrome chặn site khác mở thẳng).
                                Trong setting → tìm site <strong>dichtailieu.vn</strong> → đổi sang "Allow".
                            </p>
                        </div>
                    </details>
                    <div className="mt-5 pt-5 border-t border-slate-100 text-left space-y-2">
                        <div className="flex items-start gap-2 text-xs text-slate-500">
                            <span className="text-emerald-600 font-bold shrink-0">✓</span>
                            <span>Dự án lưu an toàn trên máy bạn — không upload lên server</span>
                        </div>
                        <div className="flex items-start gap-2 text-xs text-slate-500">
                            <span className="text-emerald-600 font-bold shrink-0">✓</span>
                            <span>Backup, share bằng zip, đồng bộ Google Drive dễ dàng</span>
                        </div>
                        <div className="flex items-start gap-2 text-xs text-slate-500">
                            <span className="text-emerald-600 font-bold shrink-0">✓</span>
                            <span>Không dùng được trên iOS Safari — cần Chrome/Edge/Firefox desktop</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full p-6 overflow-auto flex flex-col">{/* banner cũ bỏ — gate hard ở trên đã xử lý */}
            <style>{`
                @keyframes tour-arrow-bounce-x{0%,100%{transform:translateY(-50%) translateX(0)}50%{transform:translateY(-50%) translateX(6px)}}
                .tour-arrow-bounce{animation:tour-arrow-bounce-x 0.9s ease-in-out infinite}
                @keyframes tour-pulse-scale{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
                @keyframes tour-pulse-ring{0%{outline:3px solid rgba(99,102,241,0.9);outline-offset:2px}50%{outline:3px solid rgba(99,102,241,0.2);outline-offset:8px}100%{outline:3px solid rgba(99,102,241,0.9);outline-offset:2px}}
                .tour-pulse-button{animation:tour-pulse-scale 1.2s ease-in-out infinite,tour-pulse-ring 1.2s ease-in-out infinite;position:relative;z-index:10001}
                /* Chỉ ẩn footer driver khi popover có class tour-list-popover (riêng tour ProjectsList, KHÔNG ảnh hưởng Stage4 tour) */
                .driver-popover.tour-list-popover .driver-popover-footer{display:none !important}
            `}</style>
            {tourActive && (
                <button
                    onClick={() => {
                        driverRef.current?.destroy();
                        tourEnd();
                    }}
                    className="fixed bottom-6 right-6 z-[10001] px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm font-semibold rounded-full shadow-lg hover:bg-slate-50 hover:text-slate-800 transition-colors"
                >
                    Bỏ qua hướng dẫn
                </button>
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6 shrink-0">
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-2xl font-bold text-slate-800">{textMap.title}</h1>
                        {!isTrainMode && (
                            <button
                                onClick={() => {
                                    // Bước 1: start tour — highlight nút "Dự án mới" (chưa tạo demo, chỉ khi user xác nhận modal mới tạo)
                                    useTourStore.getState().start();
                                    setTimeout(() => {
                                        driverRef.current = driver({
                                            popoverClass: 'tour-list-popover', showProgress: false,
                                            allowClose: false,
                                            showButtons: [],
                                            onDestroyed: () => {
                                                if (useTourStore.getState().step === 'create-button') {
                                                    useTourStore.getState().end();
                                                }
                                            },
                                            steps: [{
                                                element: '[data-tour="create-project"]',
                                                popover: {
                                                    title: 'Bước 1/4 — Bắt đầu thôi! 👋',
                                                    description: 'Bấm nút <b>"Dự án mới"</b> đang nháy ở bên phải để bắt đầu.',
                                                    side: 'left' as const,
                                                },
                                            }],
                                        });
                                        driverRef.current.drive();
                                    }, 200);
                                }}
                                className="p-1.5 rounded-full text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                title="Xem hướng dẫn dùng dự án"
                            >
                                <HelpCircle className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{textMap.subtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            if (tourStep === 'create-button') {
                                // Tour mode: đóng tooltip driver, advance step → modal tự mở
                                driverRef.current?.destroy();
                                tourAdvance('create-modal');
                            }
                            onCreateProject(); // This will trigger default modal (select)
                        }}
                        data-tour="create-project"
                        className={`flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-100 ${tourStep === 'create-button' ? 'tour-pulse-button' : ''}`}
                    >
                        <FolderPlus className="w-4 h-4" />
                        {textMap.btnNew}
                    </button>
                </div>
            </div>

            {/* Projects Grid */}
            <div className="flex-1 pb-4">
                {projects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                        <Folder className="w-12 h-12 text-slate-300 mb-3" />
                        <p className="text-slate-500 font-medium">{textMap.emptyTitle}</p>
                        <p className="text-sm text-slate-400 mt-1">{textMap.emptySub}</p>
                        <button
                            onClick={() => onCreateProject()}
                            className="mt-4 flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 active:scale-95"
                        >
                            <FolderPlus className="w-4 h-4" />
                            {textMap.btnCreate}
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{textMap.colName}</th>
                                    <th className="px-5 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wider w-32">Ngôn ngữ</th>
                                    <th className="px-5 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wider w-24">Số file</th>
                                    <th className="px-5 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wider w-32">Tiến độ</th>
                                    <th className="px-5 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wider w-32">Cập nhật</th>
                                    <th className="px-5 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wider w-16"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {pagedProjects.map(project => {
                                    const stats = getProjectStats(project.id);
                                    const progressPercent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
                                    const isDemoRowOnTour = tourStep === 'select-demo' && project.id === DEMO_PROJECT_ID;
                                    return (
                                        <tr
                                            key={project.id}
                                            data-tour={isDemoRowOnTour ? 'demo-row' : undefined}
                                            onClick={() => {
                                                if (tourStep === 'select-demo' && project.id === DEMO_PROJECT_ID) {
                                                    tourAdvance('in-detail');
                                                    driverRef.current?.destroy();
                                                    localStorage.removeItem('tour_stage4_completed');
                                                    window.dispatchEvent(new CustomEvent('restart-stage4-tour'));
                                                }
                                                onSelectProject(project.id);
                                            }}
                                            className={`${project.type === 'translate' ? 'hover:bg-indigo-50/50' : 'hover:bg-amber-50/50'} cursor-pointer transition-colors group relative ${isDemoRowOnTour ? 'ring-2 ring-purple-400 bg-purple-50/40' : ''}`}
                                        >
                                            <td className="px-5 py-4 relative">
                                                {isDemoRowOnTour && (
                                                    <ArrowRight className="absolute -left-1 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-600 tour-arrow-bounce z-10" />
                                                )}
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 ${project.type === 'translate' ? 'bg-indigo-100 text-indigo-600' : 'bg-amber-100 text-amber-600'} rounded-lg flex items-center justify-center shrink-0 relative`}>
                                                        {project.type === 'translate' ? (
                                                            <Languages className="w-4 h-4" />
                                                        ) : (
                                                            <BrainCircuit className="w-4 h-4" />
                                                        )}
                                                        <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${project.type === 'translate' ? 'bg-indigo-500' : 'bg-amber-500'}`} />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <div className={`font-bold text-slate-800 transition-colors ${project.type === 'translate' ? 'group-hover:text-indigo-600' : 'group-hover:text-amber-600'}`}>
                                                                {project.name || 'Untitled Project'}
                                                            </div>
                                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${project.type === 'translate'
                                                                ? 'bg-indigo-100 text-indigo-600'
                                                                : 'bg-amber-100 text-amber-600'
                                                                }`}>
                                                                {project.type || 'train'}
                                                            </span>
                                                            {project.isDemo && (
                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-purple-100 text-purple-600">
                                                                    <Sparkles className="w-2.5 h-2.5" />
                                                                    Demo
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-slate-400 mt-0.5">
                                                            Created {new Date(project.createdAt).toLocaleDateString('vi-VN')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 text-center">
                                                {(() => {
                                                    const src = getLanguage(project.sourceLang || 'vi');
                                                    const tgt = getLanguage(project.targetLang || 'en');
                                                    if (!src || !tgt) return <span className="text-xs text-slate-300">—</span>;
                                                    return (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-50 border border-slate-200 rounded">
                                                            <img
                                                                src={`https://flagcdn.com/16x12/${src.countryCode}.png`}
                                                                srcSet={`https://flagcdn.com/32x24/${src.countryCode}.png 2x`}
                                                                width="16" height="12"
                                                                alt=""
                                                                loading="lazy"
                                                                className="rounded-[2px]"
                                                            />
                                                            <span className="text-[10px] font-semibold text-slate-600">{src.code.toUpperCase()}</span>
                                                            <ArrowRight className="w-2.5 h-2.5 text-slate-400" />
                                                            <img
                                                                src={`https://flagcdn.com/16x12/${tgt.countryCode}.png`}
                                                                srcSet={`https://flagcdn.com/32x24/${tgt.countryCode}.png 2x`}
                                                                width="16" height="12"
                                                                alt=""
                                                                loading="lazy"
                                                                className="rounded-[2px]"
                                                            />
                                                            <span className="text-[10px] font-semibold text-slate-600">{tgt.code.toUpperCase()}</span>
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-5 py-4 text-center">
                                                <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                                                    <FileText className="w-4 h-4 text-slate-400" />
                                                    {stats.total}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex flex-col items-center gap-1">
                                                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full transition-all ${progressPercent === 100
                                                                ? 'bg-emerald-500'
                                                                : (project.type === 'translate' ? 'bg-indigo-500' : 'bg-amber-500')
                                                                }`}
                                                            style={{ width: `${progressPercent}%` }}
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs">
                                                        {stats.completed > 0 && (
                                                            <span className="text-emerald-600 flex items-center gap-1">
                                                                <CheckCircle className="w-3 h-3" />
                                                                {stats.completed}
                                                            </span>
                                                        )}
                                                        {stats.total - stats.completed > 0 && (
                                                            <span className="text-amber-500 flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />
                                                                {stats.total - stats.completed}
                                                            </span>
                                                        )}
                                                        {stats.total === 0 && (
                                                            <span className="text-slate-400">No files</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 text-center text-sm text-slate-500">
                                                {(() => {
                                                    const lastActivity = getProjectLastActivity(project);
                                                    const diffMs = Date.now() - lastActivity.getTime();
                                                    const diffMin = Math.floor(diffMs / 60000);
                                                    if (diffMin < 1) return <span className="text-emerald-600 font-medium">Vừa xong</span>;
                                                    if (diffMin < 60) return <span>{diffMin} phút trước</span>;
                                                    const diffHr = Math.floor(diffMin / 60);
                                                    if (diffHr < 24) return <span>{diffHr} giờ trước</span>;
                                                    const diffDay = Math.floor(diffHr / 24);
                                                    if (diffDay < 7) return <span>{diffDay} ngày trước</span>;
                                                    return <span className="text-slate-400">{lastActivity.toLocaleDateString('vi-VN')}</span>;
                                                })()}
                                            </td>
                                            <td className="px-5 py-4 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {onDeleteProject && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDeleteProject(project.id);
                                                            }}
                                                            className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                                                            title="Delete Project"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <ChevronRight className={`w-5 h-5 text-slate-300 transition-colors ${project.type === 'translate' ? 'group-hover:text-indigo-500' : 'group-hover:text-amber-500'}`} />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between">
                                <button
                                    onClick={() => setPage((p: number) => Math.max(0, p - 1))}
                                    disabled={safePage === 0}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-emerald-700 hover:bg-white rounded-md disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-colors"
                                >
                                    <ChevronLeft className="w-3.5 h-3.5" /> Trước
                                </button>

                                <div className="flex items-center gap-1">
                                    {Array.from({ length: totalPages }, (_, i) => i).map(i => (
                                        <button
                                            key={i}
                                            onClick={() => setPage(i)}
                                            className={`w-7 h-7 rounded-md text-xs font-bold tabular-nums transition-colors ${
                                                i === safePage
                                                    ? 'bg-emerald-600 text-white'
                                                    : 'text-slate-500 hover:bg-white hover:text-emerald-700'
                                            }`}
                                        >
                                            {i + 1}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={() => setPage((p: number) => Math.min(totalPages - 1, p + 1))}
                                    disabled={safePage >= totalPages - 1}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-emerald-700 hover:bg-white rounded-md disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-600 transition-colors"
                                >
                                    Sau <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>

                )}
            </div>

        </div>
    );
};

export default ProjectsList;
